// Keyword sweep — the app side.
//
// A sweep is a monthly pass per SEO client that ends in the content queue as proposals a person
// approves or denies. Code does the counting (scripts/lib/keyword-sweep.mjs: Ahrefs inside a unit
// budget, KD vs DR, already-ranking, dedupe, deny rules); an agent claiming an ai_jobs row of kind
// 'sweep' does the judgment (tier, cluster, pitch) from the client's KB and service pages.
//
// This file is everything the Content Queue screen does with that:
//   - queue a sweep now (the same job the monthly schedule queues)
//   - approve / deny a proposal — a deny is a rule the next sweep reads, never just a status
//   - reorder the queue (position)
//   - hand an approved row on: landing pages, optimizations and tools go to the Spec queue as a
//     request for a spec; articles go to the content writer

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import { enqueueContentJob } from './contentJobs';
import type { Database } from './database.types';
import type { BadgeTone } from '../components/ui';
import type { JobBrief, SweepBrief } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';

export type ContentQueueRow = Database['public']['Tables']['client_content_queue']['Row'];
export type SweepSettings = Database['public']['Tables']['seo_sweep_settings']['Row'];
export type SweepRun = Database['public']['Tables']['seo_sweep_runs']['Row'];
export type SweepRule = Database['public']['Tables']['seo_sweep_rules']['Row'];

export type Tier = 'A' | 'B';
export type ContentType = 'landing_page' | 'landing_page_optimise' | 'article' | 'tool';
export type DenyScope = 'keyword' | 'topic' | 'client_never';

export type SweepService = { name: string; url?: string | null; terms?: string[] };

/** client_content_queue.brief on a proposal. Every field optional: hand-added rows have none. */
export type ProposalBrief = {
  keyword?: string;
  cluster?: string[];
  topic?: string;
  volume?: number | null;
  kd?: number | null;
  dr?: number | null;
  kd_cap?: number | null;
  intents?: string[];
  intended_url?: string | null;
  is_new_page?: boolean;
  pitch?: string;
  tier_reason?: string;
  serp_note?: string | null;
  position_now?: number | null;
  source?: 'sweep' | 'ideation' | 'manual';
  run_id?: string | null;
};

export const TIERS: Record<Tier, { label: string; tone: BadgeTone; blurb: string }> = {
  A: { label: 'A', tone: 'accent', blurb: 'Commercial, transactional or local intent for a service they sell. A landing page.' },
  B: { label: 'B', tone: 'neutral', blurb: 'Relevant, rankable, unlikely to convert. An article or a tool that builds authority.' },
};

export const CONTENT_TYPES: Record<ContentType, { label: string; short: string; tone: BadgeTone }> = {
  landing_page: { label: 'Landing page', short: 'Landing', tone: 'accent' },
  landing_page_optimise: { label: 'Optimize page', short: 'Optimize', tone: 'info' },
  article: { label: 'Article', short: 'Article', tone: 'neutral' },
  tool: { label: 'Tool', short: 'Tool', tone: 'warn' },
};

export const DENY_SCOPES: Record<DenyScope, { label: string; blurb: (pattern: string) => string }> = {
  keyword: { label: 'This keyword', blurb: (p) => `Never propose “${p}” again.` },
  topic: { label: 'This topic', blurb: (p) => `Nothing about “${p}” — the topic itself or any keyword containing it.` },
  client_never: { label: 'This client, never', blurb: (p) => `Nothing containing “${p}”, in any keyword or topic, for good.` },
};

export const SWEEP_RUN_STATUSES: Record<string, { label: string; tone: BadgeTone }> = {
  queued: { label: 'Queued', tone: 'neutral' },
  discovering: { label: 'Discovering', tone: 'warn' },
  candidates_ready: { label: 'Writing proposals', tone: 'warn' },
  proposed: { label: 'Proposed', tone: 'up' },
  capped: { label: 'Stopped at unit cap', tone: 'warn' },
  failed: { label: 'Failed', tone: 'down' },
};

export const contentTypeOf = (value: string | null | undefined) =>
  (value && CONTENT_TYPES[value as ContentType]) || null;
export const tierOf = (value: string | null | undefined) => (value && TIERS[value as Tier]) || null;
export const briefOf = (row: Pick<ContentQueueRow, 'brief'>): ProposalBrief =>
  row.brief && typeof row.brief === 'object' && !Array.isArray(row.brief) ? (row.brief as ProposalBrief) : {};

/** Landing pages, optimizations and tools are code in the client's site: they go to the Spec queue. */
export const goesToBuildQueue = (contentType: string | null | undefined) =>
  contentType === 'landing_page' || contentType === 'landing_page_optimise' || contentType === 'tool';

/** Same fold as seo_sweep_normalize() in the database, so the panel shows the pattern the rule stores. */
export function normalizeKeyword(text: string): string {
  return (text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      if (w.length > 4 && w.endsWith('ies')) return `${w.slice(0, -3)}y`;
      if (w.length > 3 && /(ch|sh|s|x|z)es$/.test(w)) return w.slice(0, -2);
      if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
      return w;
    })
    .join(' ');
}

const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

function noonTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString();
}

const monthLabel = (d = new Date()) => d.toLocaleString('en-US', { month: 'long', year: 'numeric' });

/* ────────────────────────── reading ────────────────────────── */

export async function fetchQueue(clientId: string): Promise<ContentQueueRow[]> {
  const { data, error } = await supabase
    .from('client_content_queue')
    .select('*')
    .eq('client_id', clientId)
    .order('position', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export type SweepState = {
  settings: SweepSettings | null;
  lastRun: SweepRun | null;
  /** A sweep job for this client that has not finished. Run-now is off while one exists. */
  openJob: Pick<AiJob, 'id' | 'status' | 'claimed_by'> | null;
  rules: SweepRule[];
};

const OPEN_SWEEP_STATUSES = ['spec_queued', 'spec_in_progress', 'spec_revision'];

export async function fetchSweepState(clientId: string): Promise<SweepState> {
  const [settings, runs, jobs, rules] = await Promise.all([
    supabase.from('seo_sweep_settings').select('*').eq('client_id', clientId).maybeSingle(),
    supabase.from('seo_sweep_runs').select('*').eq('client_id', clientId).order('started_at', { ascending: false }).limit(1),
    supabase
      .from('ai_jobs')
      .select('id, status, claimed_by')
      .eq('client_id', clientId)
      .eq('kind', 'sweep')
      .in('status', OPEN_SWEEP_STATUSES)
      .limit(1),
    supabase.from('seo_sweep_rules').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
  ]);
  for (const r of [settings, runs, jobs, rules]) if (r.error) throw r.error;
  return {
    settings: settings.data ?? null,
    lastRun: runs.data?.[0] ?? null,
    openJob: (jobs.data?.[0] as SweepState['openJob']) ?? null,
    rules: rules.data ?? [],
  };
}

/** The jobs queue rows were handed to, keyed by id, for the status chip on each row. */
export async function fetchBuildJobs(ids: string[]): Promise<Map<string, AiJob>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return new Map();
  const { data, error } = await supabase.from('ai_jobs').select('*').in('id', unique);
  if (error) throw error;
  return new Map(((data ?? []) as AiJob[]).map((j) => [j.id, j]));
}

/* ────────────────────────── queue order ────────────────────────── */

/**
 * Persist a new order. Only rows whose position actually changed are written, and each write is
 * read back: an RLS-blocked update returns no error and no rows, which would otherwise look saved.
 */
export async function persistOrder(ordered: Pick<ContentQueueRow, 'id' | 'position'>[]): Promise<void> {
  const changed = ordered.map((row, index) => ({ id: row.id, from: row.position, to: index })).filter((r) => r.from !== r.to);
  const results = await Promise.all(
    changed.map((r) => supabase.from('client_content_queue').update({ position: r.to }).eq('id', r.id).select('id'))
  );
  const failed = results.find((r) => r.error || (r.data?.length ?? 0) !== 1);
  if (failed) throw failed.error ?? new Error('The new order was not saved.');
}

/** The position a hand-added row takes: the bottom of the queue. */
export async function nextPosition(clientId: string): Promise<number> {
  const { data, error } = await supabase
    .from('client_content_queue')
    .select('position')
    .eq('client_id', clientId)
    .not('position', 'is', null)
    .order('position', { ascending: false })
    .limit(1);
  if (error) throw error;
  const max = data?.[0]?.position;
  return typeof max === 'number' ? Math.floor(max) + 1 : 0;
}

/* ────────────────────────── approve / deny ────────────────────────── */

async function updateRow(id: string, patch: Database['public']['Tables']['client_content_queue']['Update']): Promise<ContentQueueRow> {
  const { data, error } = await supabase.from('client_content_queue').update(patch).eq('id', id).select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The change was not saved.');
  return data[0];
}

export const approveRow = (row: ContentQueueRow) => updateRow(row.id, { status: 'queue_approved' });

export type DenyInput = {
  row: ContentQueueRow;
  reason: string;
  scope: DenyScope;
  /** What the rule matches: the keyword, the topic, or the phrase — as the person typed it. */
  pattern: string;
  userId: string | null;
};

/**
 * Deny = a rule + the status. The rule goes first: a row marked denied with no rule behind it is
 * the one failure this feature exists to prevent ("a deny that doesn't change the next run is a bug").
 * A rule that already exists for the same scope and pattern is reused, not duplicated.
 */
export async function denyRow(input: DenyInput): Promise<{ row: ContentQueueRow; rule: SweepRule }> {
  const reason = input.reason.trim();
  const pattern = normalizeKeyword(input.pattern);
  if (wordCount(reason) < 3) throw new Error('Give a reason of at least three words.');
  if (!pattern) throw new Error('The rule needs a keyword or phrase to match.');

  const { data: existing, error: findError } = await supabase
    .from('seo_sweep_rules')
    .select('*')
    .eq('client_id', input.row.client_id)
    .eq('scope', input.scope)
    .eq('pattern', pattern)
    .maybeSingle();
  if (findError) throw findError;

  let rule = existing;
  if (!rule) {
    const { data, error } = await supabase
      .from('seo_sweep_rules')
      .insert({ client_id: input.row.client_id, scope: input.scope, pattern, reason, source_queue_id: input.row.id, created_by: input.userId })
      .select('*');
    if (error) throw error;
    if (!data?.length) throw new Error('The deny rule was not saved.');
    rule = data[0];
  }

  const row = await updateRow(input.row.id, { status: 'queue_denied', deny_reason: reason, deny_scope: input.scope });
  return { row, rule };
}

export async function removeRule(id: string): Promise<void> {
  const { data, error } = await supabase.from('seo_sweep_rules').delete().eq('id', id).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('The rule was not removed.');
}

/* ────────────────────────── run now ────────────────────────── */

export type SweepJobBrief = SweepBrief;

/**
 * What the agent reads when it claims a sweep job: the "keyword-sweep" prompt (Agents → Prompts). It is the whole
 * procedure, so a sweep can be worked with no skill of its own. The monthly schedule renders the same saved prompt
 * through scripts/lib/keyword-sweep.mjs (sweepQueueNote).
 */
export function formatSweepQueueNote(brief: SweepJobBrief, template: string = PROMPT_DEFAULTS['keyword-sweep']): string {
  return renderPrompt(template, { client: brief.client_name, run_id: brief.run_id });
}

/**
 * Queue a sweep for one client now: a run row, a task, an ai_jobs row of kind 'sweep'. The same
 * three rows the monthly schedule writes, so a manual run and a scheduled one are indistinguishable.
 */
export async function enqueueSweepJob(input: {
  clientId: string;
  clientName: string;
  createdBy: { id: string; name: string };
}): Promise<AiJob> {
  // The prompt first: a run row with no job behind it would block this month's scheduled sweep.
  const template = await loadPromptTemplate('keyword-sweep');
  const { data: settings } = await supabase.from('seo_sweep_settings').select('unit_cap').eq('client_id', input.clientId).maybeSingle();
  const { data: run, error: runError } = await supabase
    .from('seo_sweep_runs')
    .insert({ client_id: input.clientId, status: 'queued', unit_cap: settings?.unit_cap ?? 50000 })
    .select('id')
    .single();
  if (runError) throw runError;

  const brief: SweepJobBrief = { client_id: input.clientId, client_name: input.clientName, run_id: run.id };
  const title = `Keyword sweep — ${input.clientName} — ${monthLabel()}`;
  const note = formatSweepQueueNote(brief, template);

  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: input.clientId,
      title,
      description: note,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: input.createdBy.id,
      assigned_to: input.createdBy.id,
      assigned_to_name: input.createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: input.createdBy.id,
    queue_note: note,
    kind: 'sweep',
    brief,
  });

  const { error: linkError } = await supabase.from('seo_sweep_runs').update({ job_id: job.id }).eq('id', run.id);
  if (linkError) throw new Error(`Job ${job.id} is queued, but the run was not linked to it: ${linkError.message}`);
  return job;
}

export async function saveSweepSettings(clientId: string, patch: Database['public']['Tables']['seo_sweep_settings']['Update']) {
  const { data, error } = await supabase
    .from('seo_sweep_settings')
    .upsert({ client_id: clientId, ...patch }, { onConflict: 'client_id' })
    .select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('Settings were not saved.');
  return data[0];
}

/* ────────────────────────── hand-off ────────────────────────── */

type Handoff = { row: ContentQueueRow; clientName: string; createdBy: { id: string; name: string } };

const handoffTitle = (row: ContentQueueRow, brief: ProposalBrief) => {
  if (row.content_type === 'landing_page_optimise') return `Optimize — ${brief.intended_url || row.recommended_url || row.title}`;
  if (row.content_type === 'tool') return `Tool — ${row.title}`;
  return `Landing page — ${row.title}`;
};

/**
 * The instruction a "Send to build queue" job carries. It asks for a SPEC, never a build: the page
 * still goes through spec approval like any other change to a client's site.
 */
const repoLineOf = (repos: { name: string; repo_url: string }[]) =>
  repos.length === 0
    ? 'Repo: no repo on file for this client — say so in the spec.'
    : repos.length === 1
      ? `Repo: ${repos[0].name} (${repos[0].repo_url})`
      : `Repos on file (pick the one that serves the live site, and say why): ${repos.map((r) => `${r.name} (${r.repo_url})`).join('; ')}`;

export function formatBuildInstruction(input: {
  row: ContentQueueRow;
  clientName: string;
  repos: { name: string; repo_url: string }[];
  template?: string;
}): string {
  const { row, clientName, repos } = input;
  const b = briefOf(row);
  const keyword = row.target_keyword || b.keyword || row.title;
  const cluster = (b.cluster ?? []).filter((k) => k && k !== keyword);
  const url = b.intended_url || row.recommended_url || '(propose one)';
  const what =
    row.content_type === 'landing_page_optimise'
      ? `an optimization of ${url}`
      : row.content_type === 'tool'
        ? 'an interactive tool'
        : 'a new landing page';
  const kdLine = [
    row.volume != null || b.volume != null ? `Volume ${(row.volume ?? b.volume)?.toLocaleString('en-US')}/mo` : null,
    row.difficulty != null || b.kd != null ? `KD ${row.difficulty ?? b.kd}` : 'KD unknown',
    b.dr != null ? `site DR ${b.dr}${b.kd_cap != null ? `, cap ${b.kd_cap}` : ''}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const repoLine = repoLineOf(repos);
  const isNew = row.content_type === 'landing_page_optimise' ? false : b.is_new_page !== false;

  return renderPrompt(input.template ?? PROMPT_DEFAULTS['send-to-build'], {
    what,
    client: clientName,
    target: `"${keyword}"${cluster.length ? ` — cluster: ${cluster.join(', ')}` : ''}.${b.intents?.length ? ` Intent: ${b.intents.join(', ')}.` : ''} ${kdLine}.`,
    intended_url: `${url} (${isNew ? 'new page' : `existing page${b.position_now != null ? `, currently ranking ${b.position_now} for "${keyword}"` : ''}`})`,
    context: [b.tier_reason ? `Why this tier: ${b.tier_reason}.` : null, b.pitch ? `Pitch: ${b.pitch}` : null, b.serp_note ? `SERP: ${b.serp_note}` : null]
      .filter(Boolean)
      .join('\n'),
    type_item:
      row.content_type === 'landing_page_optimise'
        ? '8. What changes on the live page and what stays.'
        : row.content_type === 'tool'
          ? '8. The tool itself: inputs, the formula or logic and its source, outputs, and how a result leads to the CTA.'
          : '',
    repo: repoLine,
    row_id: row.id,
  });
}

/** A new landing page goes to the queue as a Page Build; optimizations and tools stay General Fulfillment. */
export const isPageBuildRow = (row: Pick<ContentQueueRow, 'content_type'>) => row.content_type === 'landing_page';

/** The "send-to-build-page" prompt: a new landing page as a Page Build (keyword given, sections as direction). */
export function formatPageBuildInstruction(input: {
  row: ContentQueueRow;
  clientName: string;
  repos: { name: string; repo_url: string }[];
  template?: string;
}): string {
  const { row, clientName, repos } = input;
  const b = briefOf(row);
  const keyword = row.target_keyword || b.keyword || row.title;
  const cluster = (b.cluster ?? []).filter((k) => k && normalizeKeyword(k) !== normalizeKeyword(keyword));
  const kdLine = [
    row.volume != null || b.volume != null ? `Volume ${(row.volume ?? b.volume)?.toLocaleString('en-US')}/mo` : null,
    row.difficulty != null || b.kd != null ? `KD ${row.difficulty ?? b.kd}` : 'KD unknown',
    b.dr != null ? `site DR ${b.dr}${b.kd_cap != null ? `, cap ${b.kd_cap}` : ''}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return renderPrompt(input.template ?? PROMPT_DEFAULTS['send-to-build-page'], {
    client: clientName,
    keyword,
    secondary: cluster.length ? cluster.slice(0, 4).join(', ') : '(none in the cluster: add 0–4 with real volume, or none)',
    target: `"${keyword}"${b.intents?.length ? ` · intent ${b.intents.join(', ')}` : ''} · ${kdLine}`,
    intended_url: `${b.intended_url || row.recommended_url || '(propose one)'} (new page)`,
    context: [b.tier_reason ? `Why this tier: ${b.tier_reason}.` : null, b.pitch ? `Pitch: ${b.pitch}` : null, b.serp_note ? `SERP: ${b.serp_note}` : null]
      .filter(Boolean)
      .join('\n'),
    repo: repoLineOf(repos),
    row_id: row.id,
  });
}

/**
 * Landing page / optimization / tool → a Spec-queue job asking for a spec. The row moves to being_built. A new landing
 * page is a Page Build (task_type page_build: keyword + sections at spec, one agent per section at build); an
 * optimization or a tool is General Fulfillment.
 */
export async function sendToBuildQueue({ row, clientName, createdBy }: Handoff): Promise<AiJob> {
  if (row.build_job_id) throw new Error('This entry already went to a job.');
  const { data: repos, error: repoError } = await supabase
    .from('client_repos')
    .select('name, repo_url')
    .eq('client_id', row.client_id)
    .order('created_at', { ascending: true });
  if (repoError) throw repoError;

  const title = handoffTitle(row, briefOf(row));
  const pageBuild = isPageBuildRow(row);
  const instruction = pageBuild
    ? formatPageBuildInstruction({ row, clientName, repos: repos ?? [], template: await loadPromptTemplate('send-to-build-page') })
    : formatBuildInstruction({ row, clientName, repos: repos ?? [], template: await loadPromptTemplate('send-to-build') });
  const b = briefOf(row);
  // page_url is where `cockpit ai section-prompt` looks for the page; page_build marks the job for Agents → Prompts.
  const brief = pageBuild
    ? ({ page_url: b.intended_url || row.recommended_url || null, page_build: { content_queue_row: row.id, keyword: row.target_keyword || b.keyword || row.title } } as unknown as JobBrief)
    : undefined;

  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: row.client_id,
      title,
      description: instruction,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: createdBy.id,
    queue_note: instruction,
    kind: 'fulfillment',
    task_type: pageBuild ? 'page_build' : 'general',
    ...(brief ? { brief } : {}),
  });

  await updateRow(row.id, { status: 'being_built', build_job_id: job.id }).catch((err: Error) => {
    throw new Error(`Job ${job.id} is queued, but the entry still reads as approved: ${err.message}`);
  });
  return job;
}

/** Article → the content writer (content-article + the content-seo-review gate). The row moves to being_built. */
export async function sendToWriter({ row, clientName, createdBy }: Handoff): Promise<AiJob> {
  if (row.build_job_id) throw new Error('This entry already went to a job.');
  const b = briefOf(row);
  const primary = row.target_keyword || b.keyword || '';
  if (!primary) throw new Error('Add a target keyword before sending this to the writer.');
  const job = await enqueueContentJob({
    title: row.title,
    primary,
    secondary: (b.cluster ?? []).filter((k) => normalizeKeyword(k) !== normalizeKeyword(primary)).slice(0, 8),
    clientId: row.client_id,
    clientName,
    // Same default as the Content page: no repo, the article lands in Content → Pieces and is
    // deployed from there. A person picks the publish target at deploy, not here.
    repoId: null,
    repoName: null,
    repoUrl: null,
    files: [],
    createdBy,
  });
  await updateRow(row.id, { status: 'being_built', build_job_id: job.id }).catch((err: Error) => {
    throw new Error(`Job ${job.id} is queued, but the entry still reads as approved: ${err.message}`);
  });
  return job;
}

/* ────────────────────────── Ideation → content queue ────────────────────────── */

type OpportunityForQueue = {
  id: string;
  client_id: string;
  query: string;
  url: string | null;
  tier: string | null;
  tier_reason: string | null;
  action_type: string | null;
  recommendation: string | null;
  content_queue_id: string | null;
  baseline: { position?: number | null } | null;
};

/**
 * What a diagnosed finding becomes in the content queue, or null when it is not content.
 * new_page is a new landing page (A) or an article (B); expand on an A finding is an optimization of
 * the page that ranks. Title / meta / internal-link fixes stay on Ideation's own accept path.
 */
export function contentTypeForOpportunity(o: Pick<OpportunityForQueue, 'action_type' | 'tier'>): ContentType | null {
  if (o.action_type === 'new_page') return o.tier === 'A' ? 'landing_page' : 'article';
  if (o.action_type === 'expand' && o.tier === 'A') return 'landing_page_optimise';
  return null;
}

/** "Add to Content Queue" on a diagnosed Ideation finding. The finding moves to queued and links the row. */
export async function addOpportunityToQueue(o: OpportunityForQueue, userId: string | null): Promise<ContentQueueRow> {
  if (o.content_queue_id) throw new Error('This finding is already in the content queue.');
  const contentType = contentTypeForOpportunity(o);
  if (!contentType) throw new Error('Only a new page, or expanding an A-tier page, goes to the content queue.');
  const position = await nextPosition(o.client_id);
  const isNew = contentType !== 'landing_page_optimise';
  const brief: ProposalBrief = {
    keyword: o.query,
    cluster: [o.query],
    topic: o.query,
    intended_url: isNew ? null : o.url,
    is_new_page: isNew,
    pitch: o.recommendation ?? undefined,
    tier_reason: o.tier_reason ?? undefined,
    position_now: o.baseline?.position ?? null,
    source: 'ideation',
  };
  const { data, error } = await supabase
    .from('client_content_queue')
    .insert({
      client_id: o.client_id,
      title: o.query,
      target_keyword: o.query,
      recommended_url: isNew ? null : o.url,
      status: 'queue_submitted',
      source: 'manual',
      content_type: contentType,
      tier: o.tier === 'A' || o.tier === 'B' ? o.tier : null,
      position,
      brief,
      seo_opportunity_id: o.id,
      created_by: userId,
    })
    .select('*');
  if (error) throw error;
  if (!data?.length) throw new Error('The entry was not added.');
  const row = data[0];
  const { error: linkError } = await supabase
    .from('seo_opportunities')
    .update({ content_queue_id: row.id, status: 'queued' })
    .eq('id', o.id);
  if (linkError) throw new Error(`Added to the queue, but the finding was not linked: ${linkError.message}`);
  return row;
}
